import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import Sections from 'shared/components/WelcomeBack/Sections';
import Header from 'shared/components/WelcomeBack/Header';
import Footer from 'shared/components/WelcomeBack/Footer';
import BbPageHeader from 'shared/components/BbPageHeader';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { Answer, checkReportIsReady } from './Review/utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import {
  faClipboardListCheck,
  faClipboardCheck,
} from '@fortawesome/pro-light-svg-icons';
import { Report } from 'shared/components/WelcomeBack/utils';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { getSection } from '../constants';
import { isReportPassed } from './IndicatorDetails/utils';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { isBbUserView } from 'shared/utils/partnerUser';

const footerHeading = 'Future tasks';
const footerIntroText =
  'We’ll let you know (via email and the To Do section of your account) when it’s time to make these updates.';
const footerTasks = [
  {
    id: 1,
    name: 'Program Midpoint',
    text: 'This is a brief check-in to update your program’s progress.',
    icon: faClipboardListCheck,
  },
  {
    id: 2,
    name: 'Program End',
    text: 'This is the final update detailing your program’s success.',
    icon: faClipboardCheck,
  },
];

interface DashboardStartProps {
  report: Report;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sections: any[];
  answers: Answer[];
  isThirdParty: boolean;
  loadingQuestions: boolean;
  isLoading: boolean;
  handleSectionChange: (sectionIndex: number, optionIndex: number) => unknown;
}

const StyledDiv = styled.div`
  flex: 1;
  background-color: ${COLORS.whiteBase};
  .report-sections {
    margin-bottom: ${SPACERS.extraLarge};
  }
`;

const isBbPartner = (): boolean => isBbUserView();

const DashboardStart: React.FC<DashboardStartProps> = ({
  report,
  answers,
  sections,
  isLoading,
  isThirdParty,
  loadingQuestions,
  handleSectionChange,
}) => {
  const { impactSummary, impactDetails } = useIndicator();
  const isPassed = isReportPassed(
    report.endDate,
    Number(report.year) || undefined,
    report.stage,
  );
  const incompleteSections = useMemo(
    () => checkReportIsReady(answers, impactSummary, impactDetails, isPassed),
    [answers, impactSummary, impactDetails, isPassed],
  );

  const handleChange = useCallback(
    (sectionId: FeedbackSectionId) => {
      const option = getSection(sectionId, sections);
      if (!option) return;
      handleSectionChange(0, option.index);
    },
    [handleSectionChange, sections],
  );

  if (isLoading) {
    return (
      <StyledDiv>
        <TrueImpactLoading />
      </StyledDiv>
    );
  }

  return (
    <StyledDiv>
      <BbPageHeader
        title="Impact Reporting"
        subtitle={
          answers.length === 0
            ? 'Welcome to your report! Let’s get started.'
            : 'Your report is coming along nicely. Let’s keep going!'
        }
      />
      {!isBbPartner() && (
        <Header report={report} isThirdParty={isThirdParty} />
      )}
      <div className="report-sections">
        <Sections
          report={report}
          isReportPassed={isPassed}
          isLoading={loadingQuestions}
          isNewReport={answers.length === 0}
          incomplete={incompleteSections.object}
          isReportReadyToSubmit={incompleteSections.list.length === 0}
          handleSectionChange={handleChange}
        />
      </div>
      {!isThirdParty && (
        <Footer
          tasks={footerTasks}
          heading={footerHeading}
          intro={footerIntroText}
        />
      )}
    </StyledDiv>
  );
};

export default DashboardStart;
